import { _lcs, locale } from "../i18n";

/**
 * Region — the guest-app's view of what there is to do around the house.
 *
 * Three feeds behind one screen: restaurants and sights come from
 * `GET /place/:accommodationId?type=gastro|poi`, routes from
 * `GET /tour/:accommodationId`. All three are public payloads, already ordered
 * NEAREST FIRST by the API (the accommodation's own coordinates, else the
 * centre of its region) — which is the only reason a bounded list is the right
 * bounded list: the regional pool holds thousands of records spread across a
 * whole Bundesland.
 *
 * Same split of duties as core/Events.js: the API returns data (enum keys,
 * numbers, ISO strings) and this turns it into wording, a distance and
 * a gradient. Categories are loaded lazily and cached — a guest who only ever
 * taps "Restaurants" never pays for four thousand sights.
 *
 * Without a resolved tenant there is nothing to show. Unlike the events feed
 * there is no demo fallback: bundled sample restaurants would be a lie about a
 * real region, and "noch keine Einträge" is an honest answer.
 */
export class Region
{
    /**
     * How many records to pull per category. The list is nearest-first, so
     * this is a radius in disguise: enough to browse and to fill a map, far
     * short of the four thousand the region actually holds.
     */
    static PAGE_SIZE = 60;

    /**
     * The three chips, in the order the design puts them. `countKey` is the
     * sentence above the list — "12 Touren in der Nähe", not "12 Orte". It is a
     * whole sentence rather than a noun because pluralising is not a matter of
     * picking one of two words in Russian or Czech.
     *
     * The German wording here IS the dictionary key: these are class constants,
     * evaluated before I18n exists, so every reader translates on the way out.
     */
    static CATEGORIES = [
        { id : "food",   label : "Restaurants",        icon : "utensils", kind : "place", type : "gastro",
          countKey : "{count} Adressen in der Nähe" },
        { id : "sights", label : "Sehenswürdigkeiten", icon : "landmark", kind : "place", type : "poi",
          countKey : "{count} Orte in der Nähe" },
        { id : "tours",  label : "Touren",             icon : "route",    kind : "tour",
          countKey : "{count} Touren in der Nähe" }
    ];

    /**
     * Place category -> label (German source = dictionary key). The same
     * curated vocabulary the saas-admin offers as filters; a record may carry
     * a key that is not here
     * (importers map provider vocabularies into the column freely — see
     * model/Place), which is why an unknown key falls back to the type rather
     * than showing a raw enum to a guest.
     */
    static CATEGORY_TITLES = {
        // Gastronomie
        restaurant : "Restaurant", cafe : "Café", bar : "Bar", bakery : "Bäckerei",
        winery : "Weingut", brewery : "Brauerei", beer_garden : "Biergarten",
        mountain_hut : "Berghütte", ice_cream : "Eisdiele", farm_shop : "Hofladen",
        // Kultur & Geschichte
        sight : "Sehenswertes", historic_site : "Historische Stätte",
        historic_centre : "Historischer Kern", monument : "Denkmal",
        castle : "Burg & Schloss", ruin : "Ruine", church : "Kirche",
        monastery : "Kloster", museum : "Museum", gallery : "Galerie",
        architecture : "Architektur", mill : "Mühle",
        // Natur
        nature : "Natur", viewpoint : "Aussichtspunkt", park : "Park & Garten",
        lake : "See", waterfall : "Wasserfall", cave : "Höhle", geotope : "Geotop",
        nature_info : "Naturinformation",
        // Freizeit
        playground : "Spielplatz", swimming : "Bad & Badestelle",
        wellness : "Wellness & Sauna", zoo : "Zoo & Tierpark",
        theatre : "Theater & Bühne", cinema : "Kino", culture_venue : "Veranstaltungsort",
        club : "Club", sports : "Sport", guided_tour : "Führung",
        // Service
        tourist_info : "Tourist-Information", shop : "Geschäft", library : "Bücherei",
        boat_rental : "Bootsverleih", transport : "Verkehr", parking : "Parkmöglichkeit",
        toilet : "WC", other : "Sonstiges"
    };

    /** Category -> gradient key (`.ga-photo--<key>`) for records with no image. */
    static PHOTOS = {
        restaurant : "wine", cafe : "ember", bar : "dusk", bakery : "ember",
        winery : "wine", brewery : "ember", beer_garden : "valley",
        mountain_hut : "valley", ice_cream : "lavender", farm_shop : "valley",
        castle : "culture", church : "culture", monastery : "culture",
        museum : "culture", gallery : "culture", monument : "culture",
        viewpoint : "valley", nature : "valley", park : "valley",
        lake : "coast", waterfall : "coast", cave : "dusk"
    };

    /** Category -> map/list glyph. */
    static ICONS = {
        restaurant : "utensils", cafe : "coffee", bar : "wine", bakery : "croissant",
        winery : "wine", brewery : "flame", beer_garden : "leaf",
        mountain_hut : "mountain", ice_cream : "snowflake", farm_shop : "leaf",
        castle : "landmark", church : "landmark", monastery : "landmark",
        museum : "landmark", gallery : "image", monument : "landmark",
        viewpoint : "mountain", nature : "leaf", park : "flower-2",
        lake : "waves", waterfall : "droplets", cave : "mountain",
        playground : "footprints", swimming : "waves", zoo : "paw-print",
        theatre : "ticket", cinema : "tv", sports : "bike"
    };

    static ACTIVITY_TITLES = {
        hiking : "Wanderung", walking : "Spaziergang", cycling : "Radtour",
        road_cycling : "Rennrad", mountain_biking : "Mountainbike",
        pilgrimage : "Pilgerweg", paddling : "Kanutour", horse_riding : "Reittour",
        running : "Laufstrecke", climbing : "Klettertour", winter_hiking : "Winterwanderung",
        snowshoe : "Schneeschuhtour", cross_country_skiing : "Langlaufloipe",
        sightseeing : "Stadtrundgang", other : "Tour"
    };

    static ACTIVITY_ICONS = {
        hiking : "footprints", walking : "footprints", cycling : "bike",
        road_cycling : "bike", mountain_biking : "bike", paddling : "ship",
        horse_riding : "route", running : "footprints", climbing : "mountain",
        winter_hiking : "snowflake", snowshoe : "snowflake",
        cross_country_skiing : "snowflake", sightseeing : "landmark", other : "route"
    };

    static DIFFICULTY_TITLES = {
        easy : "Leicht", moderate : "Mittel", difficult : "Schwer", expert : "Sehr schwer"
    };

    static ROUTE_TYPE_TITLES = {
        loop : "Rundweg", point_to_point : "Streckentour", out_and_back : "Hin und zurück"
    };

    constructor( app )
    {
        this.app = app;

        // Category id -> { items, loaded, failed }. One entry per chip.
        this._cache = {};
        this._pending = {};

        Region.CATEGORIES.forEach( ( category ) =>
        {
            this._cache[ category.id ] = { items : [], loaded : false, failed : false };
        } );

        // The host's own picks — one payload for all three kinds, shared by the
        // home rail and the "Empfehlungen" screen so the two never disagree.
        this._recommendations = { gastro : [], poi : [], tour : [], loaded : false, failed : false };
        this._recommendationsPending = null;
    }

    /* ----------------------------- Recommendations --------------------------- */

    /**
     * What the host singled out for their guests
     * (GET /accommodation/:id/recommendations).
     *
     * A different thing from the category feeds above: those are "everything
     * the region offers that this house lets through", this is "the handful the
     * host actually points at". Cached and de-duplicated the same way — the
     * home screen and the Empfehlungen screen share one request.
     */
    async loadRecommendations( force = false )
    {
        if ( !force && this._recommendations.loaded )
        {
            return this._recommendations;
        }
        if ( this._recommendationsPending )
        {
            return await this._recommendationsPending;
        }

        this._recommendationsPending = this._fetchRecommendations()
            .finally( () => { this._recommendationsPending = null; } );

        return await this._recommendationsPending;
    }

    async _fetchRecommendations()
    {
        const accommodation = this.app.accommodation;
        const state = this._recommendations;

        if ( !accommodation )
        {
            state.loaded = true;
            state.failed = false;
            return state;
        }

        try
        {
            const { result } = await this.app.api.get( "/accommodation/" + accommodation.id + "/recommendations" );
            if ( result && 200 === +result.status && result.data )
            {
                state.gastro = result.data.gastro || [];
                state.poi = result.data.poi || [];
                state.tour = result.data.tour || [];
                state.loaded = true;
                state.failed = false;
                return state;
            }
        }
        catch ( e )
        {
            console.error( e );
        }

        state.failed = true;
        return state;
    }

    /**
     * The recommendations as cards.
     *
     * Round-robin across the three kinds rather than concatenated, because the
     * home rail only shows the first few — and five restaurants followed by
     * nothing else would misrepresent what the host picked.
     *
     * @param {string|null} kind  "gastro" | "poi" | "tour" to narrow, null for all.
     */
    recommendationCards( kind = null )
    {
        const state = this._recommendations;
        const toCards = ( list, mapper ) => list.map( mapper );

        const buckets = {
            gastro : toCards( state.gastro, Region.placeToCard ),
            poi : toCards( state.poi, Region.placeToCard ),
            tour : toCards( state.tour, Region.tourToCard )
        };

        if ( kind )
        {
            return buckets[ kind ] || [];
        }

        const order = [ "gastro", "poi", "tour" ];
        const mixed = [];
        const longest = Math.max( ...order.map( ( key ) => buckets[ key ].length ), 0 );

        for ( let i = 0; i < longest; i++ )
        {
            order.forEach( ( key ) =>
            {
                if ( buckets[ key ][ i ] ) mixed.push( buckets[ key ][ i ] );
            } );
        }
        return mixed;
    }

    /** True when the recommendations could not be loaded (vs. genuinely none). */
    recommendationsFailed()
    {
        return this._recommendations.failed === true;
    }

    /** How many of each kind the host recommended — drives the filter chips. */
    recommendationCounts()
    {
        const state = this._recommendations;
        return {
            gastro : state.gastro.length,
            poi : state.poi.length,
            tour : state.tour.length,
            total : state.gastro.length + state.poi.length + state.tour.length
        };
    }

    /* -------------------------------- Loading -------------------------------- */

    static category( categoryId )
    {
        return Region.CATEGORIES.find( ( c ) => c.id === categoryId ) || Region.CATEGORIES[ 0 ];
    }

    /** True while no tenant is resolved — the screen then has nothing to show. */
    isDemo()
    {
        return !this.app.accommodation;
    }

    state( categoryId )
    {
        return this._cache[ categoryId ] || { items : [], loaded : false, failed : false };
    }

    /**
     * Resolve one category. Concurrent callers share one request; never throws
     * — a failed load leaves whatever was there and flags `failed` so the
     * screen can say so rather than showing an empty list as if the region
     * were empty.
     */
    async load( categoryId, force = false )
    {
        const state = this.state( categoryId );

        if ( !force && state.loaded )
        {
            return state.items;
        }
        if ( this._pending[ categoryId ] )
        {
            return await this._pending[ categoryId ];
        }

        this._pending[ categoryId ] = this._fetch( categoryId )
            .finally( () => { this._pending[ categoryId ] = null; } );

        return await this._pending[ categoryId ];
    }

    async _fetch( categoryId )
    {
        const state = this._cache[ categoryId ];
        const category = Region.category( categoryId );
        const accommodation = this.app.accommodation;

        if ( !accommodation )
        {
            state.loaded = true;
            state.failed = false;
            return state.items;
        }

        const endpoint = category.kind === "tour"
            ? "/tour/" + accommodation.id + "?limit=" + Region.PAGE_SIZE
            : "/place/" + accommodation.id + "?type=" + category.type + "&limit=" + Region.PAGE_SIZE;

        try
        {
            const { result } = await this.app.api.get( endpoint );
            if ( result && 200 === +result.status && Array.isArray( result.data ) )
            {
                state.items = result.data;
                state.loaded = true;
                state.failed = false;
                return state.items;
            }
        }
        catch ( e )
        {
            console.error( e );
        }

        state.failed = true;
        return state.items;
    }

    /** One category as list/map cards, in the order the API returned them. */
    getCards( categoryId )
    {
        const category = Region.category( categoryId );
        return this.state( categoryId ).items.map( ( item ) => category.kind === "tour"
            ? Region.tourToCard( item )
            : Region.placeToCard( item ) );
    }

    /**
     * One entry as a detail view model. Served from the loaded list when it is
     * there, otherwise fetched — a guest may arrive by deep link or reload the
     * page on a detail screen.
     */
    async getDetail( kind, id )
    {
        const accommodation = this.app.accommodation;
        if ( !accommodation )
        {
            return null;
        }

        const endpoint = kind === "tour"
            ? "/tour/" + accommodation.id + "/" + id
            : "/place/" + accommodation.id + "/" + id;

        try
        {
            const { result } = await this.app.api.get( endpoint );
            if ( result && 200 === +result.status && result.data )
            {
                return kind === "tour"
                    ? Region.tourToDetail( result.data )
                    : Region.placeToDetail( result.data );
            }
        }
        catch ( e )
        {
            console.error( e );
        }
        return null;
    }

    /* ------------------------------ Presentation ----------------------------- */

    /** API place -> list/map card. */
    static placeToCard( place )
    {
        const key = Region.primaryCategory( place );
        const cover = Region.cover( place );

        return {
            id        : place.id,
            kind      : "place",
            // gastro | poi — what colours the map pin.
            type      : place.type,
            title     : place.name,
            cat       : Region.CATEGORY_TITLES[ key ]
                ? _lcs( Region.CATEGORY_TITLES[ key ] )
                : ( place.type === "gastro" ? _lcs( "Gastronomie" ) : _lcs( "Sehenswürdigkeit" ) ),
            meta      : Region.metaLine( place ),
            distance  : Region.distanceText( place.distanceKm ),
            open      : Region.openingLine( place ),
            icon      : Region.ICONS[ key ] || ( place.type === "gastro" ? "utensils" : "landmark" ),
            photo     : Region.PHOTOS[ key ] || ( place.type === "gastro" ? "wine" : "culture" ),
            image     : cover ? cover.url : "",
            imageAlt  : cover ? cover.alt : "",
            latitude  : Region.number( place.latitude ),
            longitude : Region.number( place.longitude )
        };
    }

    /** API tour -> list/map card. The trailhead is what a pin marks. */
    static tourToCard( tour )
    {
        const cover = Region.cover( tour );

        return {
            id        : tour.id,
            kind      : "tour",
            title     : tour.name,
            cat       : _lcs( Region.ACTIVITY_TITLES[ tour.activityType ] || "Tour" ),
            meta      : Region.tourMetaLine( tour ),
            distance  : Region.distanceText( tour.startDistanceKm ),
            open      : Region.tourConditionLine( tour ),
            icon      : Region.ACTIVITY_ICONS[ tour.activityType ] || "route",
            photo     : "valley",
            image     : cover ? cover.url : "",
            imageAlt  : cover ? cover.alt : "",
            latitude  : Region.number( tour.startLatitude ),
            longitude : Region.number( tour.startLongitude )
        };
    }

    static placeToDetail( place )
    {
        const card = Region.placeToCard( place );

        return {
            ...card,
            subtitle    : place.subtitle || "",
            description : ( place.description || place.teaser || "" ).trim(),
            images      : Region.images( place ),
            facts       : Region.placeFacts( place ),
            hours       : Region.openingHours( place ),
            openingNote : place.openingNote || "",
            address     : Region.address( place.location ),
            links       : Region.placeLinks( place ),
            track       : null,
            sourceUrl   : place.sourceUrl || ""
        };
    }

    static tourToDetail( tour )
    {
        const card = Region.tourToCard( tour );
        const coordinates = tour.geometry && Array.isArray( tour.geometry.coordinates )
            ? tour.geometry.coordinates
            : [];

        return {
            ...card,
            subtitle    : tour.subtitle || "",
            description : ( tour.description || tour.teaser || "" ).trim(),
            images      : Region.images( tour ),
            facts       : Region.tourFacts( tour ),
            hours       : [],
            openingNote : tour.conditionNote || "",
            address     : ( tour.startPoint && ( tour.startPoint.address || tour.startPoint.name ) ) || "",
            links       : Region.tourLinks( tour ),
            // GeoJSON [ lon, lat ] — the map flips it, nothing else touches it.
            track       : coordinates,
            sourceUrl   : tour.sourceUrl || ""
        };
    }

    /**
     * The category a card is labelled with: the first known key wins, so a
     * Weingut filed as [ 'winery', 'sight' ] reads "Weingut" and not "Sehenswertes".
     */
    static primaryCategory( place )
    {
        const categories = Array.isArray( place.categories ) ? place.categories : [];
        return categories.find( ( key ) => Region.CATEGORY_TITLES[ key ] ) || categories[ 0 ] || "";
    }

    static images( record )
    {
        const list = Array.isArray( record.images ) ? record.images : [];
        return list
            .filter( ( image ) => image && image.url )
            .map( ( image ) => ( {
                url    : image.url,
                alt    : image.alt || record.name || "",
                credit : image.credit || ""
            } ) );
    }

    static cover( record )
    {
        const list = Region.images( record );
        return list.length > 0 ? list[ 0 ] : null;
    }

    /** "650 m" / "2,4 km" / "" — how far from the house. */
    static distanceText( distanceKm )
    {
        const value = Number( distanceKm );
        if ( !Number.isFinite( value ) )
        {
            return "";
        }
        if ( value < 1 )
        {
            // Below a kilometre a guest thinks in metres, and "0,3 km" is how
            // nobody describes a three-minute walk.
            return _lcs( "{n} m", { n : Math.max( Math.round( value * 1000 / 10 ) * 10, 10 ) } );
        }
        return _lcs( "{n} km", {
            n : value.toLocaleString( locale(), { maximumFractionDigits : 1 } )
        } );
    }

    /** The town, or the price level when there is no town — never empty air. */
    static metaLine( place )
    {
        const parts = [];

        const city = place.location && place.location.city;
        if ( city ) parts.push( city );

        const priceLevel = place.attributes && place.attributes.priceLevel;
        if ( Number.isFinite( +priceLevel ) && +priceLevel > 0 )
        {
            parts.push( "€".repeat( Math.min( +priceLevel, 4 ) ) );
        }

        return parts.join( " · " );
    }

    /** "11 km · 4:00 h" — the two numbers a route is picked by. */
    static tourMetaLine( tour )
    {
        const parts = [];

        if ( Number.isFinite( +tour.distanceKm ) )
        {
            parts.push( _lcs( "{n} km", {
                n : ( +tour.distanceKm ).toLocaleString( locale(), { maximumFractionDigits : 1 } )
            } ) );
        }

        const duration = Region.durationText( tour.durationMinutes );
        if ( duration ) parts.push( duration );

        const difficulty = Region.DIFFICULTY_TITLES[ tour.difficulty ];
        if ( difficulty ) parts.push( _lcs( difficulty ) );

        return parts.join( " · " );
    }

    static durationText( minutes )
    {
        const total = Math.round( +minutes );
        if ( !Number.isFinite( total ) || total <= 0 )
        {
            return "";
        }
        const hours = Math.floor( total / 60 );
        const rest = total % 60;
        return hours > 0 ? `${hours}:${String( rest ).padStart( 2, "0" )} h` : `${rest} min`;
    }

    /**
     * Today's opening line. Deliberately not "geöffnet"/"geschlossen": the feed
     * gives weekday ranges without holidays or seasonal closures, and a guest
     * who walks half an hour on the strength of a green "geöffnet" that turns
     * out to be wrong is worse off than one who read the hours and phoned.
     */
    static openingLine( place )
    {
        const today = Region.openingHours( place ).find( ( day ) => day.isToday );
        if ( today && today.hours )
        {
            return _lcs( "Heute {hours}", { hours : today.hours } );
        }
        if ( today )
        {
            return _lcs( "Heute geschlossen" );
        }
        return ( place.openingNote || "" ).trim();
    }

    /** Day.getDay() index -> the schema.org key the records are stored under. */
    static WEEKDAY_KEYS = [ "su", "mo", "tu", "we", "th", "fr", "sa" ];

    /**
     * Opening hours as one row per weekday, starting TODAY rather than on
     * Monday: a guest reading this is deciding about now, not filing a week.
     *
     * The stored shape is schema.org's openingHoursSpecification —
     * [ { days: ['mo','tu'], opens: '11:00', closes: '22:00' } ] — where one
     * day may appear in several entries (lunch and dinner) and a day in none is
     * a closing day.
     */
    static openingHours( place )
    {
        const entries = Array.isArray( place.openingHours ) ? place.openingHours : [];
        if ( entries.length === 0 )
        {
            return [];
        }

        const now = new Date();
        const today = now.getDay();

        return Array.from( { length : 7 }, ( _, offset ) =>
        {
            const index = ( today + offset ) % 7;
            const key = Region.WEEKDAY_KEYS[ index ];
            // A real date rather than a lookup table, so Intl can name the day
            // in whatever language the guest is reading.
            const date = new Date( now.getFullYear(), now.getMonth(), now.getDate() + offset );

            const hours = entries
                .filter( ( entry ) => Array.isArray( entry.days ) && entry.days.includes( key ) && entry.opens )
                .map( ( entry ) => entry.closes
                    ? _lcs( "{from}–{to} Uhr", { from : entry.opens, to : entry.closes } )
                    : _lcs( "ab {from} Uhr", { from : entry.opens } ) )
                .join( ", " );

            return {
                label   : date.toLocaleDateString( locale(), { weekday : "short" } ),
                hours   : hours,
                isToday : offset === 0
            };
        } );
    }

    static address( location )
    {
        const place = location || {};
        return [
            place.street,
            [ place.postalCode, place.city ].filter( Boolean ).join( " " )
        ].filter( ( part ) => part && String( part ).trim() ).join( ", " );
    }

    /** The practical facts of a place, in the order a guest asks them. */
    static placeFacts( place )
    {
        const facts = [];
        const attributes = place.attributes || {};

        const cuisines = Array.isArray( attributes.cuisines ) ? attributes.cuisines : [];
        if ( cuisines.length > 0 )
        {
            facts.push( { icon : "utensils", text : cuisines.slice( 0, 3 ).join( ", " ) } );
        }

        if ( Number.isFinite( +attributes.visitDurationMinutes ) && +attributes.visitDurationMinutes > 0 )
        {
            facts.push( { icon : "clock", text : _lcs( "ca. {duration}", {
                duration : Region.durationText( attributes.visitDurationMinutes )
            } ) } );
        }

        if ( attributes.isOutdoor === true )
        {
            facts.push( { icon : "cloud-sun", text : _lcs( "Im Freien" ) } );
        }

        const price = Region.priceText( place.pricing );
        if ( price )
        {
            facts.push( { icon : "badge-euro", text : price } );
        }

        if ( Array.isArray( place.accessibility ) && place.accessibility.length > 0 )
        {
            facts.push( { icon : "check-circle-2", text : _lcs( "Barrierefrei zugänglich" ) } );
        }

        return facts;
    }

    static tourFacts( tour )
    {
        const facts = [];

        if ( Number.isFinite( +tour.ascentMeters ) && +tour.ascentMeters > 0 )
        {
            facts.push( { icon : "trending-up", text : _lcs( "{n} hm Aufstieg", {
                n : Math.round( +tour.ascentMeters )
            } ) } );
        }

        const routeType = Region.ROUTE_TYPE_TITLES[ tour.routeType ];
        if ( routeType )
        {
            facts.push( { icon : "route", text : _lcs( routeType ) } );
        }

        if ( tour.isDogFriendly )
        {
            facts.push( { icon : "paw-print", text : _lcs( "Hunde erlaubt" ) } );
        }
        if ( tour.isStrollerFriendly )
        {
            facts.push( { icon : "footprints", text : _lcs( "Kinderwagentauglich" ) } );
        }

        const price = Region.priceText( tour.pricing );
        if ( price )
        {
            facts.push( { icon : "badge-euro", text : price } );
        }

        return facts;
    }

    static tourConditionLine( tour )
    {
        const seasons = {
            spring : "Frühling", summer : "Sommer", autumn : "Herbst", winter : "Winter"
        };

        const list = ( Array.isArray( tour.seasons ) ? tour.seasons : [] )
            .map( ( season ) => seasons[ season ] )
            .filter( Boolean )
            .map( ( season ) => _lcs( season ) );

        if ( list.length === 4 || list.length === 0 )
        {
            return "";
        }
        return list.join( ", " );
    }

    /** "Eintritt frei" / "ab 12,00 €" / "" — same vocabulary as the event feed. */
    static priceText( pricing )
    {
        if ( !pricing || !pricing.type )
        {
            return "";
        }

        const currency = pricing.currency || "EUR";
        const amount = ( value ) => Number( value ).toLocaleString( locale(), {
            style : "currency", currency : currency, maximumFractionDigits : 2
        } );

        switch ( pricing.type )
        {
            case "free":     return _lcs( "Eintritt frei" );
            case "donation": return _lcs( "Eintritt auf Spendenbasis" );
            case "fixed":    return pricing.priceMin != null ? amount( pricing.priceMin ) : "";
            case "from":     return pricing.priceMin != null
                ? _lcs( "ab {amount}", { amount : amount( pricing.priceMin ) } ) : "";
            case "range":
                if ( pricing.priceMin != null && pricing.priceMax != null )
                {
                    return amount( pricing.priceMin ) + " – " + amount( pricing.priceMax );
                }
                return pricing.priceMin != null
                    ? _lcs( "ab {amount}", { amount : amount( pricing.priceMin ) } ) : "";
            default: return "";
        }
    }

    static placeLinks( place )
    {
        const contact = place.contact || {};
        const links = [];

        if ( contact.phone )
        {
            links.push( { url : "tel:" + contact.phone, label : contact.phone, icon : "phone" } );
        }
        if ( contact.website )
        {
            links.push( { url : contact.website, label : _lcs( "Zur Website" ), icon : "globe", external : true } );
        }
        if ( links.length === 0 && place.sourceUrl )
        {
            links.push( { url : place.sourceUrl, label : _lcs( "Mehr erfahren" ), icon : "arrow-right", external : true } );
        }
        return links;
    }

    static tourLinks( tour )
    {
        const links = [];

        if ( tour.gpxUrl )
        {
            links.push( { url : tour.gpxUrl, label : _lcs( "GPX-Track laden" ), icon : "download", external : true } );
        }
        if ( tour.websiteUrl )
        {
            links.push( { url : tour.websiteUrl, label : _lcs( "Zur Website" ), icon : "globe", external : true } );
        }
        if ( links.length === 0 && tour.sourceUrl )
        {
            links.push( { url : tour.sourceUrl, label : _lcs( "Mehr erfahren" ), icon : "arrow-right", external : true } );
        }
        return links;
    }

    /** Number(null) is 0, and 0°N is off the coast of Africa. */
    static number( value )
    {
        if ( value === null || value === undefined || value === "" )
        {
            return null;
        }
        const parsed = Number( value );
        return Number.isFinite( parsed ) ? parsed : null;
    }
}
